import numOrDefault from 'lib/helpers/numOrDefault';
import { ia, iaRa, inRn } from 'lib/helpers/utility';

import { amountToCents } from '../../Claims/CMS1500/lib/amountToCents';
import {
  denied_codes,
  isPriorPayerAdjudication,
  isStrictPatientRouting,
  patient_responsibility,
  provider_responsibility,
  sumStampedAdjustments
} from '../../lib/insuranceAdjustmentGroups';

import { renderType } from './renderType';
import { calcLatestBenefits } from './calcLatestBenefits';

export const formatProcedureAdjustments = ({ procDetails, adjustments }) => {
  const adjustmentsLength = adjustments?.length - 1 || 0;

  if (procDetails?.denied) {
    return {
      ...procDetails,
      ins_adjustment: 0,
      ins_payment: 0,
      ptBalance: numOrDefault(procDetails?.charge),
      ins_balance: 0
    };
  }

  let insAdj = 0;
  let insBalance = 0;
  let ptAdj = 0;
  let priorPayerAdj = 0;

  const lastEntry = adjustments[adjustmentsLength];

  if (isStrictPatientRouting(lastEntry)) {
    const {
      ptAdj: stampedPtAdj,
      insAdj: stampedInsAdj,
      priorPayerAdj: stampedPriorPayerAdj
    } = sumStampedAdjustments(lastEntry);
    ptAdj += stampedPtAdj;
    insAdj += stampedInsAdj;
    priorPayerAdj += stampedPriorPayerAdj;
  } else {
    adjustments[adjustmentsLength]?.ins_adj?.forEach((item) => {
      const { group, code, amount, type = '' } = item;

      if (isPriorPayerAdjudication(item)) {
        priorPayerAdj += amountToCents(amount);
        return;
      }

      const hasPRCode = patient_responsibility[group]?.includes(+code) || type == 'pt_rep';
      const hasDeniedCode = denied_codes[group]?.includes(code);
      const isNotPR = provider_responsibility[group]?.includes(+code);

      if (type == 'ins_balance') {
        insBalance += amountToCents(amount);
      }

      if (isNotPR || (group !== 'PR' && !hasPRCode && !hasDeniedCode) || type == 'adj') {
        insAdj += amountToCents(amount);
      }
    });

    adjustments[adjustmentsLength]?.ins_adj?.forEach((item) => {
      const { group, code, amount, type = '' } = item;

      if (isPriorPayerAdjudication(item)) return;

      if (type == 'adj' || type == 'ins_balance') return;

      const hasPRCode = patient_responsibility[group]?.includes(+code) || type == 'pt_rep';
      const hasDeniedCode = denied_codes[group]?.includes(code);
      const isNotPR = provider_responsibility[group]?.includes(+code);

      if (!isNotPR && (group === 'PR' || !!hasPRCode || !!hasDeniedCode)) {
        ptAdj += amountToCents(amount);
      }
    });

    if (ia(adjustments[adjustmentsLength]?.pt_adj)) {
      adjustments[adjustmentsLength]?.pt_adj?.forEach((item) => {
        const { group, code, amount, type = '' } = item;

        if (isPriorPayerAdjudication(item)) {
          priorPayerAdj += amountToCents(amount);
          return;
        }

        const hasPRCode = patient_responsibility[group]?.includes(+code) || type == 'pt_rep';
        const hasDeniedCode = denied_codes[group]?.includes(code);
        const isNotPR = provider_responsibility[group]?.includes(+code);

        if (!isNotPR && (group === 'PR' || !!hasPRCode || !!hasDeniedCode)) {
          ptAdj += amountToCents(amount);
        }
      });
    }
  }

  const ins_balance =
    numOrDefault(procDetails?.charge) -
    numOrDefault(insAdj) -
    numOrDefault(adjustments[adjustmentsLength]?.paid) -
    numOrDefault(procDetails?.other_adj) -
    numOrDefault(ptAdj) -
    numOrDefault(priorPayerAdj) +
    numOrDefault(insBalance);

  const childrenItemRow = {
    ...procDetails,
    ins_adjustment: insAdj,
    ins_payment: adjustments[adjustmentsLength]?.paid,
    ptBalance: ptAdj,
    ins_balance
  };

  return childrenItemRow;
};

const TIERS = [
  { key: 'primary', label: 'Primary' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'tertiary', label: 'Tertiary' }
];

const buildTierRows = ({ item, qty }) =>
  TIERS.filter(({ key }) => ia(item?.[key])).map(({ key, label }) =>
    formatProcedureAdjustments({
      procDetails: {
        details: item?.details,
        dateofservice: item?.dateofservice,
        type: label,
        qty,
        charge: item?.charge,
        ...(key === 'primary' && { denied: item?.denied })
      },
      adjustments: item?.[key]
    })
  );

export const SELF_PAY_ROW_TYPES = ['self pay', 'self_pay', 'cash'];

export const formatInvoiceProcedures = ({ invoices = [] }) => {
  const rows = [];

  iaRa(invoices).forEach((item) => {
    const isPackageOrProduct = ['package', 'product'].includes(item?.type?.toLowerCase());
    const isSelfPay = SELF_PAY_ROW_TYPES.includes(item?.type?.toLowerCase());

    const qty = inRn(item?.quantity) || inRn(item?.units) || inRn(item?.sales_count) || 1;

    const children = buildTierRows({ item, qty });

    const ins_adjustment =
      item?.isEstimated || !ia(children)
        ? item?.ins_adjustment
        : children.reduce((total, child) => total + numOrDefault(child?.ins_adjustment), 0);

    const calculatedInsBalance =
      numOrDefault(item?.charge) -
      numOrDefault(ins_adjustment) -
      numOrDefault(item?.ins_payment) -
      numOrDefault(item?.other_adj) -
      numOrDefault(item?.pt_balance);

    const ins_balance =
      isPackageOrProduct || isSelfPay
        ? 0
        : item?.isEstimated
          ? item?.charge - numOrDefault(item?.other_adj)
          : calculatedInsBalance;

    const hasInsuranceTiers = ia(item?.primary);

    const ptBalanceMultiplier = hasInsuranceTiers || !item?.type || isPackageOrProduct ? 1 : qty;

    const ptBalance =
      (item?.ptBalance
        ? item?.ptBalance
        : hasInsuranceTiers
          ? calcLatestBenefits({ procedure: item })?.ptBalance
          : 0) * ptBalanceMultiplier;

    const itemRow = {
      ...item,
      details: item?.details,
      dateofservice: item?.dateofservice,
      type: item?.primary ? renderType(item) : item?.type,
      qty,
      charge: item?.charge,
      ins_payment: item?.ins_payment,
      ptBalance,
      ins_adjustment,
      ins_balance,
      show: item?.show ?? true
    };

    if (ia(children)) {
      itemRow.children = children;
    }

    rows.push(itemRow);
  });
  return rows;
};
